// Course: cursor que encadeia seções de mapa ao longo de um heading (graus, 0 = -z).
// Rampas podem ter qualquer ângulo; plataformas/zonas são axis-aligned (usar em
// headings cardinais: 0, ±90, 180 — o construtor de mapa garante isso).
import { box, surfRamp, BuiltBrush } from './primitives';
import type { ZoneBox, StageSpawn } from './types';

const D = Math.PI / 180;

export class Course {
  built: BuiltBrush[] = [];
  zones: ZoneBox[] = [];
  stageSpawns: StageSpawn[] = [];

  constructor(public x = 0, public y = 0, public z = 0, public ang = 0) {}

  get dirX(): number { return -Math.sin(this.ang * D); }
  get dirZ(): number { return -Math.cos(this.ang * D); }

  advance(d: number): this {
    this.x += this.dirX * d;
    this.z += this.dirZ * d;
    return this;
  }

  drop(dy: number): this { this.y -= dy; return this; }
  turn(deg: number): this { this.ang += deg; return this; }

  /** Desloca o cursor perpendicular ao heading (positivo = direita). */
  shift(d: number): this {
    this.x += -this.dirZ * d;
    this.z += this.dirX * d;
    return this;
  }

  // --- ramificações (atalhos): salva/restaura o cursor ---
  private marks: { x: number; y: number; z: number; ang: number }[] = [];
  mark(): this {
    this.marks.push({ x: this.x, y: this.y, z: this.z, ang: this.ang });
    return this;
  }
  restore(): this {
    const m = this.marks.pop();
    if (m) { this.x = m.x; this.y = m.y; this.z = m.z; this.ang = m.ang; }
    return this;
  }

  /** Corredor plano com paredes baixas (seções de bhop). Cardinal. */
  corridor(halfW: number, len: number, wallH = 96, mat = 'floor'): { min: [number, number, number]; max: [number, number, number] } {
    const startX = this.x, startZ = this.z;
    const b = this.platform(halfW, len, mat);
    // paredes laterais (perpendicular ao heading)
    const wallT = 32;
    if (Math.abs(this.dirZ) > 0.5) { // heading em z
      const z0 = Math.min(startZ, this.z), z1 = Math.max(startZ, this.z);
      this.built.push(box([startX - halfW - wallT, b.min[1], z0], [startX - halfW, b.min[1] + wallH, z1], 'wall'));
      this.built.push(box([startX + halfW, b.min[1], z0], [startX + halfW + wallT, b.min[1] + wallH, z1], 'wall'));
    } else {
      const x0 = Math.min(startX, this.x), x1 = Math.max(startX, this.x);
      this.built.push(box([x0, b.min[1], startZ - halfW - wallT], [x1, b.min[1] + wallH, startZ - halfW], 'wall'));
      this.built.push(box([x0, b.min[1], startZ + halfW], [x1, b.min[1] + wallH, startZ + halfW + wallT], 'wall'));
    }
    return b;
  }

  /** Sequência de plataformas com gaps (bhop). Cardinal. */
  bhopGaps(count: number, size: number, gap: number, halfW: number, mat = 'accent'): this {
    for (let i = 0; i < count; i++) {
      this.platform(halfW, size, mat);
      if (i < count - 1) this.advance(gap);
    }
    return this;
  }

  /** Rampa de surf (prisma com pico) ao longo do heading atual; base no y atual. */
  ramp(width: number, height: number, len: number, mat = 'ramp'): this {
    const cx = this.x + this.dirX * (len / 2);
    const cz = this.z + this.dirZ * (len / 2);
    this.built.push(surfRamp([cx, this.y, cz], width, height, len, mat, this.ang));
    return this.advance(len);
  }

  /** Plataforma axis-aligned centrada no heading (cardinal), topo no y atual.
   *  Retorna os bounds para zonas. */
  platform(halfW: number, len: number, mat = 'floor', thick = 32): { min: [number, number, number]; max: [number, number, number] } {
    const x2 = this.x + this.dirX * len;
    const z2 = this.z + this.dirZ * len;
    // perpendicular ao heading (cardinal): expande nos dois eixos e deixa o
    // dominante definir o comprimento
    const minX = Math.min(this.x, x2) - (Math.abs(this.dirZ) > 0.5 ? halfW : 0);
    const maxX = Math.max(this.x, x2) + (Math.abs(this.dirZ) > 0.5 ? halfW : 0);
    const minZ = Math.min(this.z, z2) - (Math.abs(this.dirX) > 0.5 ? halfW : 0);
    const maxZ = Math.max(this.z, z2) + (Math.abs(this.dirX) > 0.5 ? halfW : 0);
    this.built.push(box([minX, this.y - thick, minZ], [maxX, this.y, maxZ], mat));
    this.advance(len);
    return { min: [minX, this.y, minZ], max: [maxX, this.y, maxZ] };
  }

  /** Zona sobre um bounds de plataforma (altura padrão 224u). */
  zoneOver(b: { min: [number, number, number]; max: [number, number, number] }, kind: ZoneBox['kind'], extra: Partial<ZoneBox> = {}): this {
    this.zones.push({
      kind,
      min: [b.min[0], b.min[1], b.min[2]],
      max: [b.max[0], b.max[1] + 320, b.max[2]],
      ...extra,
    });
    return this;
  }

  /** Marca o spawn do stage atual na posição corrente (antes de avançar!). */
  spawnHere(backoff = 96): this {
    this.stageSpawns.push({
      pos: [this.x - this.dirX * backoff, this.y + 2, this.z - this.dirZ * backoff],
      yaw: this.ang, // câmera: forward = (-sin(yaw), -cos(yaw)) — mesma convenção do heading
    });
    return this;
  }
}
